import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VoiceCallInterface } from '../components/voice/VoiceCallInterface';
import { SimpleVoiceChat } from '../components/voice/SimpleVoiceChat';
import ChatLayout from '../components/chat/layout/ChatLayout';
import { ChatInterfaceProvider } from '../contexts/ChatInterfaceContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ToggleLeft, ToggleRight } from 'lucide-react';

const VoiceChatComparison: React.FC = () => {
  const { positId } = useParams<{ positId: string }>();
  const navigate = useNavigate();
  const [useSimpleVersion, setUseSimpleVersion] = useState(true);

  const handleBack = () => {
    navigate(`/chat/${positId}`);
  };

  if (!positId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-red-600 text-lg mb-4">Invalid Posit ID</div>
        <button 
          onClick={() => navigate('/')} 
          className="px-6 py-3 bg-primary text-primary-foreground rounded-[var(--radius)] hover:bg-primary/90 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <ChatInterfaceProvider>
      <ChatLayout 
        mobileTitle="Voice Chat Comparison"
        showMobileHeader={false}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Chat
              </Button>
              <h1 className="text-lg font-semibold">Voice Chat Comparison</h1>
            </div>
            
            {/* Toggle between versions */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">ElevenLabs (Complex)</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUseSimpleVersion(!useSimpleVersion)}
                className="p-1"
              >
                {useSimpleVersion ? (
                  <ToggleRight className="h-6 w-6 text-green-600" />
                ) : (
                  <ToggleLeft className="h-6 w-6 text-gray-400" />
                )}
              </Button>
              <span className="text-sm text-gray-600">OpenAI (Simple)</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-4">
            {useSimpleVersion ? (
              <div className="max-w-2xl mx-auto">
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-green-800 mb-2">
                    ✅ OpenAI TTS + Browser Speech Recognition (Recommended)
                  </h2>
                  <p className="text-green-700 text-sm">
                    This is the simplified, more reliable approach using OpenAI's TTS API 
                    and the browser's native speech recognition. Much more stable than ElevenLabs!
                  </p>
                </div>
                <SimpleVoiceChat positId={positId} />
              </div>
            ) : (
              <div className="h-full">
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-2xl mx-auto">
                  <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                    ⚠️ ElevenLabs WebSocket (Original - Problematic)
                  </h2>
                  <p className="text-yellow-700 text-sm">
                    This is your current ElevenLabs implementation. It's complex, expensive, 
                    and prone to errors. We recommend switching to the OpenAI version.
                  </p>
                </div>
                <VoiceCallInterface 
                  positId={positId} 
                  onEndCall={handleBack}
                />
              </div>
            )}
          </div>
        </div>
      </ChatLayout>
    </ChatInterfaceProvider>
  );
};

export default VoiceChatComparison;